// The replay's move list: click a move to jump to it, with the material
// balance at the step on screen and the PGN/FEN export underneath.
import { el } from '@bn-games/ui-shell';
import { t } from '../i18n/I18n.js';
import { toFen } from '../game/fen.js';
import { sanPlies, type SanPly } from '../game/pgn.js';
import { analyzeGame, type GameAnalysis } from '../history/GameAnalysis.js';
import type { ReplayController } from '../history/ReplayController.js';
import { createExportRow } from './exportRow.js';
import { MoveListPanel } from './MoveListPanel.js';

function summaryText(analysis: GameAnalysis, step: number): string {
  const balance = analysis.balance[step] ?? 0;
  const side = t(balance > 0 ? 'common.white' : 'common.black');
  const material = balance === 0 ? t('moveList.materialEven') : t('moveList.materialLead', { side, points: Math.abs(balance) });
  return `${material} · ${t('moveList.captures', { white: analysis.captures.w, black: analysis.captures.b })}`;
}

export class ReplayPanel {
  private readonly panel: MoveListPanel;
  private readonly summary = el('div', { class: 'chess-moves__summary' });
  private readonly plies: readonly SanPly[];
  private readonly analysis: GameAnalysis;

  constructor(canvas: HTMLCanvasElement, private readonly controller: ReplayController, seek: (plies: number) => void) {
    const states = controller.getStates();
    const final = states[states.length - 1];
    this.plies = final === undefined ? [] : sanPlies(final);
    this.analysis = analyzeGame(states);
    const exportRow = createExportRow(controller.getRecord(), { fen: () => toFen(controller.getState().position) });
    this.panel = new MoveListPanel({
      canvas,
      onSelect: seek,
      // The transport bar is fixed to the bottom of the viewport.
      bottomInset: () => document.querySelector<HTMLElement>('.replay-controls')?.offsetHeight ?? 0,
      footer: el('div', { class: 'chess-moves__footer-body' }, [this.summary, exportRow]),
    });
    this.sync();
  }

  /** The step changed. */
  sync(): void {
    const step = this.controller.getStepIndex();
    this.panel.setMoves(this.plies, step);
    this.summary.textContent = summaryText(this.analysis, step);
  }

  layout(): void {
    this.panel.layout();
  }

  dispose(): void {
    this.panel.dispose();
  }
}
